import {FC, KeyboardEventHandler, useContext, useEffect, useMemo, useState} from 'react';
import {FieldValues, RegisterOptions, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useRecoilState, useRecoilStateLoadable, useRecoilValue, useSetRecoilState} from 'recoil';

import ClearIcon from '@mui/icons-material/Clear';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';
import LoadingButton from '@mui/lab/LoadingButton';
import {Button, FormControl, InputLabel, Menu, MenuItem, Select, Typography} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import {EventBusContext} from '../../../providers/EventBus';
import ControlledInput from '../../../ui/controlled-input/ControlledInput';
import Filters from '../../Filters';

import {ServiceRequestService} from '../../../api/services/serviceRequest.service';
import {searchAddressItemStrategy} from '../../../api/strategies/searchAddressItemStrategy';

import {
  currentServiceRequestsFiltersSetState,
  filtersServiceRequestsSetsState,
  filtersServiceRequestsState,
  triggerFiltersServiceRequestsQuery,
} from '../../../store/filters/atoms';
import {filtersSetsServiceRequestsQuery} from '../../../store/filters/selectors';
import {
  serviceRequestPageable,
  serviceRequestsQueryTrigger,
  serviceRequestsState,
  toolbarServiceRequestSearchString,
  toolbarServiceRequestSearchStringArchived,
} from '../../../store/service-requests/atoms';

import {statusCodeTranslation, statusCodeTranslationEntries} from '../../../shared/status.data';
import {filterServiceRequestsBy, statusCodes} from '../../Filters/constants/filters.data';

import {green} from '@mui/material/colors';
import {buttonStyle} from '../../../shared/styles';

import {searchWorkersForFilterStrategy} from '../../../api/strategies/searchWorkersStrategy';
import {AddressItemType} from '../../../shared/types/addressItem.types';
import {IDeviceTypesDict} from '../../../shared/types/deviceTypesDict.types';
import {ITaskLocationsDict} from '../../../shared/types/taskLocationsDict.types';
import {authState} from '../../../store/auth/atoms';
import {deviceTypesDictQuery} from '../../../store/deviceTypes/selectors';
import {taskLocationsMapWithNamesState} from '../../../store/taskLocations/atoms';
import {taskLocationsDictQuery} from '../../../store/taskLocations/selectors';
import {ArchiveCheckbox} from '../../ArchiveCheckbox';
import {loadableWithDefault} from '../../Filters/helpers/loadableWithDefault';
import {getIsUserCustomer, getIsUserWorker} from '../../../global/lib';
import {useCompanyTariffGuard} from '../../../modules/company-tariff';

interface ISearchBy {
  onKeyUp: KeyboardEventHandler<HTMLDivElement>;
  name: 'id' | 'extNumber' | 'deviceNumberNew';
  label: string;
  additionalFunc?: (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  rules?: Omit<RegisterOptions<FieldValues, string>, 'disabled' | 'valueAsNumber' | 'valueAsDate' | 'setValueAs'>;
  resetSearchField: () => void;
  onSubmit: () => Promise<unknown>;
}

interface ITableToolbar {
  loadingState: 'hasValue' | 'hasError' | 'loading';
  resetPageable: () => void;
}

const actions = [
  {
    label: 'Назначить',
    path: '/serviceRequests/assign',
  },
  {
    label: 'Создать',
    path: '/serviceRequests/new',
  },
];

export const TableToolbar: FC<ITableToolbar> = ({loadingState, resetPageable}) => {
  const [loading, setLoading] = useState({id: false, extNumber: false, deviceNumberNew: false});
  const [anchorElActions, setAnchorElActions] = useState<null | HTMLElement>(null);
  const [archived, setArchived] = useRecoilState(toolbarServiceRequestSearchStringArchived);
  const pageable = useRecoilValue(serviceRequestPageable);
  const filters = useRecoilValue(filtersServiceRequestsState);
  const [{state: taskLocationsState, contents: taskLocationsContents}, setTaskLocations] =
    useRecoilStateLoadable(taskLocationsDictQuery);
  const [deviceTypesLoadable] = useRecoilStateLoadable(deviceTypesDictQuery);
  const mapWithTaskLocations = useRecoilValue(taskLocationsMapWithNamesState);
  const setServiceRequests = useSetRecoilState(serviceRequestsState);
  const [searchString, setSearchString] = useRecoilState(toolbarServiceRequestSearchString);
  const triggerQuery = useSetRecoilState(triggerFiltersServiceRequestsQuery);
  const triggerSrQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const navigate = useNavigate();
  const [{contents, state}, setFiltersSets] = useRecoilStateLoadable(filtersSetsServiceRequestsQuery);
  const {bus} = useContext(EventBusContext);
  const {user} = useRecoilValue(authState);
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const [selectedSearch, setSelectedSearch] = useState<'id' | 'extNumber' | 'deviceNumberNew'>('id');

  const isCustomer = useMemo(() => getIsUserCustomer(user), [user]);
  const isWorker = useMemo(() => {
    return getIsUserWorker(user);
  }, [user]);
  const availableActions = useMemo(
    () => (isCustomer ? actions.filter((action) => action.path !== '/serviceRequests/assign') : actions),
    [isCustomer],
  );

  const {
    control,
    getValues,
    watch,
    setValue,
    formState: {errors},
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: searchString,
  });

  const openActions = Boolean(anchorElActions);

  const handleOpenActions = (event: React.MouseEvent<HTMLElement>) => {
    if (!checkTariff()) return;

    setAnchorElActions(event.currentTarget);
  };

  const handleCloseActions = () => {
    setAnchorElActions(null);
  };

  const handleActionClick = (path: string) => () => {
    if (!checkTariff()) return;

    navigate(path);
  };

  const watchData = watch();

  const resetSearchField = (entity: 'id' | 'extNumber' | 'deviceNumberNew') => () => {
    setValue(entity, '', {shouldValidate: true});
    setSearchString((prev) => ({
      ...prev,
      [entity]: '',
    }));
    clearErrors();
  };

  const keyHandler: (entity: 'id' | 'extNumber' | 'deviceNumberNew') => KeyboardEventHandler<HTMLDivElement> =
    (entity) => (e) => {
      switch (e.code) {
        case 'Escape':
          if (!loading[entity] && watchData[selectedSearch]) {
            resetSearchField(entity)();
            onSubmit(entity);
          }
          return;
        case 'Enter':
          !errors[entity] && watchData[selectedSearch] && !loading[entity] && onSubmit(entity);
          return;
      }
    };

  const onSubmit = async (entity: 'id' | 'extNumber' | 'deviceNumberNew') => {
    const data = getValues();

    try {
      setLoading((prev) => ({
        ...prev,
        [entity]: true,
      }));

      const res = await ServiceRequestService.getAllList({
        spec:
          entity === 'id' && data[entity]
            ? {
                [entity]: Number(data[entity].trim()),
              }
            : data[entity]
              ? {
                  [entity]: data[entity].trim(),
                }
              : {},
        pageable: {
          ...pageable,
          order: ['id', 'ASC'],
        },
        params: {
          //todo: проверить типы в связи с миграцией statusCode -> status
          ...Object.fromEntries(
            filters.map((i) => {
              if (i[0] === 'status' || i[0] === 'serviceType') {
                const found = statusCodeTranslationEntries.find(
                  (item) => !!item[1].match(new RegExp(typeof i[1] === 'string' ? i[1] : '', 'i')),
                );

                return [i[0], (found && found[0]) || i[1]];
              }

              return i;
            }),
          ),
          archive: archived,
        },
      });

      setServiceRequests(res.data);
    } catch (err) {
      bus?.emit('error', [err]);
    } finally {
      setLoading((prev) => ({
        ...prev,
        [entity]: false,
      }));
    }
  };

  const handleArchiveChange = (_: unknown, checked: boolean) => {
    setArchived(checked);
    triggerSrQuery((p) => p + 1);
  };

  const SEARCH_BY: Record<'id' | 'extNumber' | 'deviceNumberNew', ISearchBy> = useMemo(() => {
    return {
      id: {
        onKeyUp: keyHandler('id'),
        label: 'Поиск по номеру заявки',
        name: 'id',
        onSubmit: () => onSubmit('id'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('id');
        },
        rules: {
          pattern: {
            value: /^((\d)+)?()?$/,
            message: 'Только цифры',
          },
        },
        resetSearchField: resetSearchField('id'),
      },
      extNumber: {
        onKeyUp: keyHandler('extNumber'),
        label: 'Поиск по лицевому счету',
        name: 'extNumber',
        onSubmit: () => onSubmit('extNumber'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('extNumber');
        },
        resetSearchField: resetSearchField('extNumber'),
      },
      deviceNumberNew: {
        onKeyUp: keyHandler('deviceNumberNew'),
        label: 'Поиск по новому номеру счетчика',
        name: 'deviceNumberNew',
        onSubmit: () => onSubmit('deviceNumberNew'),
        additionalFunc: (e) => {
          e?.currentTarget?.value?.length === 0 && onSubmit('deviceNumberNew');
        },
        resetSearchField: resetSearchField('deviceNumberNew'),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyHandler]);

  useEffect(() => {
    if (state === 'hasValue') setFiltersSets(contents);

    if (taskLocationsState === 'hasValue') setTaskLocations(taskLocationsContents);

    if (state === 'hasError')
      bus?.emit('error', [
        contents,
        {
          toastId: 'taskFilterSets',
        },
      ]);

    if (taskLocationsState === 'hasError')
      bus?.emit('error', [
        taskLocationsContents,
        {
          toastId: 'taskLocations',
        },
      ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    return () => {
      const data = getValues();

      setSearchString({
        id: data.id ? data.id.trim() : data.id,
        extNumber: data.extNumber ? data.extNumber.trim() : data.extNumber,
        deviceNumberNew: data.deviceNumberNew ? data.deviceNumberNew.trim() : data.deviceNumberNew,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.length]);

  return (
    <Toolbar
      sx={{
        pl: {sm: 2},
        pr: {xs: 1, sm: 1},
        justifyContent: 'space-between',
      }}
    >
      <Stack direction="column" width="100%" spacing={1}>
        <Stack direction="row" paddingY={2} justifyContent="space-between">
          <Stack direction="row" spacing={2}>
            <ControlledInput
              {...SEARCH_BY[selectedSearch]}
              control={control}
              size="small"
              fullWidth={true}
              sx={{width: 350, mt: '10px !important'}}
              InputProps={{
                endAdornment: (
                  <>
                    {watchData[selectedSearch] ? (
                      <Tooltip title="Очистить">
                        <IconButton size="small" onClick={SEARCH_BY[selectedSearch].resetSearchField}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <LoadingButton
                      sx={{padding: '8px', minWidth: '40px'}}
                      onClick={SEARCH_BY[selectedSearch].onSubmit}
                      loading={loading[selectedSearch]}
                      disabled={!watchData[selectedSearch] || !!errors[selectedSearch]?.message}
                      size="small"
                    >
                      <SearchIcon />
                    </LoadingButton>
                  </>
                ),
              }}
            />
            <FormControl variant="standard" sx={{m: 1, minWidth: 120}}>
              <InputLabel id="searchByLabel">Критерий поиска</InputLabel>
              <Select
                labelId="searchByLabel"
                id="searchBy"
                value={selectedSearch}
                onChange={(e) => {
                  if (watchData[selectedSearch]) {
                    SEARCH_BY[selectedSearch].resetSearchField();
                    SEARCH_BY[selectedSearch].onSubmit();
                  }
                  setSelectedSearch(e.target.value as 'id' | 'extNumber' | 'deviceNumberNew');
                }}
              >
                <MenuItem value={'id'}>Номер заявки</MenuItem>
                <MenuItem value={'extNumber'}>Лицевой счет</MenuItem>
                <MenuItem value={'deviceNumberNew'}>Номер нового счетчика</MenuItem>
              </Select>
            </FormControl>
            {loadingState === 'loading' && <CircularProgress style={{marginTop: '10px'}} size="24px" />}
          </Stack>
          <Stack direction="row" gap={2} alignItems="center">
            <ArchiveCheckbox checked={archived} onChange={handleArchiveChange} disabled={isWorker} />

            {availableActions.length > 1 && (
              <Button
                sx={{
                  ...buttonStyle,
                  p: 1,
                  border: 0,
                  background: green[500],
                  '&:hover': {
                    background: green[600],
                    border: 0,
                  },
                  '&.Mui-disabled': {
                    background: green[500],
                    color: '#fff',
                    opacity: 0.6,
                    pointerEvents: 'none',
                  },
                }}
                onClick={handleOpenActions}
                endIcon={
                  openActions ? <KeyboardArrowUpIcon htmlColor="#fff" /> : <KeyboardArrowDownIcon htmlColor="#fff" />
                }
                variant="outlined"
                disabled={isWorker || tariffLoading}
              >
                <Typography sx={{color: '#fff'}}>Управление заявками</Typography>
              </Button>
            )}
            {availableActions.length === 1 && (
              <Button
                sx={{
                  ...buttonStyle,
                  p: 1,
                  border: 0,
                  background: green[500],
                  '&:hover': {
                    background: green[600],
                    border: 0,
                  },
                }}
                onClick={handleActionClick(availableActions[0].path)}
                variant="outlined"
                disabled={isWorker || tariffLoading}
              >
                <Typography sx={{color: '#fff'}}>{availableActions[0].label} заявку</Typography>
              </Button>
            )}
            <Menu
              sx={{mt: '45px'}}
              anchorEl={anchorElActions}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorElActions)}
              onClose={handleCloseActions}
            >
              {availableActions.map((action) => (
                <MenuItem
                  key={action.path}
                  onClick={handleActionClick(action.path)}
                  disabled={tariffLoading}
                  sx={{width: '204px'}}
                >
                  <Typography textAlign="center">{action.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Stack>
        </Stack>
        <Stack direction="row">
          {/* //todo: разобраться с лоадером в useSearchStrategy */}
          <Filters
            isLoading={state === 'loading'}
            filterType="SERVICE_REQUEST"
            filtersSetsState={filtersServiceRequestsSetsState}
            filtersState={filtersServiceRequestsState}
            currentFiltersSetState={currentServiceRequestsFiltersSetState}
            triggerQuery={triggerQuery}
            resetPageable={resetPageable}
            filterBy={filterServiceRequestsBy}
            dicts={{
              taskLocationCode: mapWithTaskLocations,
              status: new Map(Object.entries(statusCodeTranslation['serviceRequest'])),
            }}
            predefined={{
              taskLocationCode: Array.isArray(taskLocationsContents?.content)
                ? taskLocationsContents.content.map((item: ITaskLocationsDict) => [item.name, item.code])
                : taskLocationsContents,
              status: statusCodes,
              serviceType: loadableWithDefault<IDeviceTypesDict>(deviceTypesLoadable, []).map((item) => [
                item.name,
                item.code,
              ]),
            }}
            dictSearch={{
              city: {
                searchStrategy: (
                  (type: AddressItemType) => (str) =>
                    searchAddressItemStrategy(str, type)
                )('CITY'),
              },
              region: {
                searchStrategy: (
                  (type: AddressItemType) => (str) =>
                    searchAddressItemStrategy(str, type)
                )('REGION'),
              },
              street: {
                searchStrategy: (
                  (type: AddressItemType) => (str) =>
                    searchAddressItemStrategy(str, type)
                )('STREET'),
              },
              workerUid: {
                searchStrategy: (str) => searchWorkersForFilterStrategy(str),
              },
            }}
          />
        </Stack>
      </Stack>
    </Toolbar>
  );
};
